import type { ServiceRequestMessage } from "bonusx-api-main-manager";
import type { Getter, Setter } from "jotai";
import { useAtomValue } from "jotai";
import { useAtomCallback } from "jotai/utils";
import type { IChatMessageStore } from "../../stores/ChatMessageStore";
import { ChatMessageStore } from "../../stores/ChatMessageStore";
import { getSortedChatMessagesFromNewestToOldest } from "../../utils/getSortedChatMessages";

const initialServiceMessage: ServiceRequestMessage[] = [];

interface IUseChatMessageStore {
  appendServiceRequestMessagesWithTotalCount: (
    serviceRequestId: number,
    messages: ServiceRequestMessage[],
    totalCountForCurrentFilter?: number
  ) => void;
  messages: ServiceRequestMessage[];
  totalCountForCurrentFilter: number;
}

export function useChatMessageStore(serviceRequestId: number): IUseChatMessageStore {
  const messages = useAtomValue(ChatMessageStore);
  const appendServiceRequestMessagesWithTotalCount = useAtomCallback(function setMessagesByServiceRequestId(
    get: Getter,
    set: Setter,
    serviceRequestId: number,
    messages: ServiceRequestMessage[],
    totalCountForCurrentFilter?: number
  ) {
    const currentValue = get(ChatMessageStore);
    let newMessages = messages;
    if (currentValue[serviceRequestId]) {
      const messageDetailForServiceRequestId = currentValue[serviceRequestId];

      const currentlyStoredMessagesForServiceRequestId = messageDetailForServiceRequestId.messages;
      const newMessagesIds = messages.map((message) => message.id);

      const oldMessagesWithoutDuplicates = currentlyStoredMessagesForServiceRequestId.filter(
        function filterDuplicateMessages(message) {
          const isMessageInNewMessagesArray = newMessagesIds.includes(message.id);
          return !isMessageInNewMessagesArray;
        }
      );

      newMessages = [...oldMessagesWithoutDuplicates, ...messages];
    }

    const sortedMessages = getSortedChatMessagesFromNewestToOldest(newMessages);
    const newStoreValue: IChatMessageStore = {
      ...currentValue,
      [serviceRequestId]: {
        messages: sortedMessages,
        totalCount: totalCountForCurrentFilter ?? currentValue[serviceRequestId].totalCount,
      },
    };

    set(ChatMessageStore, newStoreValue);
  });

  const storeHandler: IUseChatMessageStore = {
    appendServiceRequestMessagesWithTotalCount,
    messages: messages[serviceRequestId]?.messages ?? initialServiceMessage,
    totalCountForCurrentFilter: messages[serviceRequestId]?.totalCount ?? 0,
  };

  return storeHandler;
}
